import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonAccordion, IonAccordionGroup, IonButton, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonSpinner, IonTitle, IonToggle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { SuperButtonComponent } from "@qid/super";
import { PlusMinusComponent } from "../../../../components/plus-minus/plus-minus.component";
import { CollectionTemplate } from '../../../collection-template';
import { TemplateSelectModalComponent } from '../../components/template-select-modal/template-select-modal.component';
import { CreateCollectionRequestDto, ParticipantDetails } from '../../dto/create-collection-request.dto';
import { CollectionRequest } from '../../interface/collection-request.interface';
import { DisplayService } from '@qid/core';
import { CollectionRequestService } from '../../services/collection-request.service';

@Component({
  selector: 'app-create-collection-request',
  templateUrl: './create-collection-request.page.html',
  styleUrls: ['./create-collection-request.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonHeader,
    IonToolbar,
    IonButton,
    IonTitle,
    IonContent,
    PlusMinusComponent,
    IonIcon,
    IonItem,
    IonToggle,
    IonAccordionGroup,
    IonAccordion,
    IonLabel,
    SuperButtonComponent,
    IonSpinner,
  ],
})
export class CreateCollectionRequestPage implements OnInit {
  allowGroup = false;
  minParticipants = 1;
  maxParticipants = 2;
  participantContacts: string[] = [];
  /** Index of the participant slot marked as admin, or null if none. */
  adminParticipantIndex: number | null = null;
  selectedTemplateId: string | null = null;
  selectedTemplate: CollectionTemplate | null = null;
  webhookUrl = '';
  webhookSecret = '';
  webhookIdentifier = '';
  loading = false;
  pageLoading = false;
  error: string | null = null;
  requestId: string | null = null;

  get isEditMode(): boolean {
    return !!this.requestId;
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Collection Request' : 'Create Collection Request';
  }

  get submitLabel(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save' : 'Create';
  }

  get participantSlots(): number[] {
    return Array.from({ length: Math.max(0, this.maxParticipants) }, (_, i) => i);
  }

  /** Request body that would be sent on Create, as formatted JSON. */
  get requestBodyJson(): string {
    return JSON.stringify(this.buildDto(), null, 2);
  }

  getParticipantContact(index: number): string {
    return this.participantContacts[index] ?? '';
  }

  setParticipantContact(index: number, value: string): void {
    const next = [...this.participantContacts];
    while (next.length <= index) next.push('');
    next[index] = value;
    this.participantContacts = next;
  }

  isParticipantAdmin(index: number): boolean {
    return this.adminParticipantIndex === index;
  }

  setParticipantAdmin(index: number, isAdmin: boolean): void {
    this.adminParticipantIndex = isAdmin ? index : (this.adminParticipantIndex === index ? null : this.adminParticipantIndex);
  }

  onMinParticipantsChange(value: number): void {
    this.minParticipants = value;
    if (this.maxParticipants < value) {
      this.maxParticipants = value;
    }
  }

  constructor(
    private location: Location,
    private modalController: ModalController,
    private collectionRequestService: CollectionRequestService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private displayService: DisplayService,
  ) { }

  ngOnInit(): void {
    this.requestId = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.requestId) {
      void this.loadRequest(this.requestId);
    }
  }

  onBack(): void {
    this.location.back();
  }

  async openTemplateSelect(): Promise<void> {
    if (this.isEditMode) {
      return;
    }
    const modal = await this.modalController.create({
      component: TemplateSelectModalComponent,
      componentProps: { selectedTemplateId: this.selectedTemplateId },
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data?.template) {
      this.onTemplateSelected(data.template);
    }
  }

  onTemplateSelected(template: CollectionTemplate): void {
    this.selectedTemplate = template;
    this.selectedTemplateId = template._id ?? null;
    this.error = null;
  }

  private participantDetailsToContact(details?: ParticipantDetails): string {
    if (!details) {
      return '';
    }
    return details.email ?? details.phoneNumber ?? '';
  }

  private populateFromRequest(request: CollectionRequest): void {
    this.selectedTemplateId = request.templateId ?? request.template?._id ?? null;
    this.selectedTemplate = request.template ?? null;
    this.minParticipants = request.minimumParticipants ?? 1;
    this.maxParticipants = request.maximumParticipants ?? Math.max(this.minParticipants, 2);
    this.allowGroup =
      this.minParticipants > 1 ||
      this.maxParticipants > 1 ||
      (request.participantList?.length ?? 0) > 0;
    this.participantContacts = [];
    this.adminParticipantIndex = null;
    const requestWithLegacyAdmin = request as CollectionRequest & {
      adminParticipantDetails?: ParticipantDetails;
    };
    const participants = [...(request.participantList ?? [])];
    if (requestWithLegacyAdmin.adminParticipantDetails) {
      participants.unshift({ ...requestWithLegacyAdmin.adminParticipantDetails, isAdmin: true });
    }
    participants.forEach((participant, index) => {
      this.setParticipantContact(index, this.participantDetailsToContact(participant));
      if (participant.isAdmin) {
        this.adminParticipantIndex = index;
      }
    });
    const requestWithWebhook = request as CollectionRequest & {
      webhookUrl?: string;
      webhookSecret?: string;
      webhookIdentifier?: string;
    };
    this.webhookUrl = requestWithWebhook.webhookUrl ?? '';
    this.webhookSecret = requestWithWebhook.webhookSecret ?? '';
    this.webhookIdentifier = requestWithWebhook.webhookIdentifier ?? '';
  }

  private async loadRequest(id: string): Promise<void> {
    this.pageLoading = true;
    this.error = null;
    try {
      const res = await this.collectionRequestService.readOne(id);
      if (res.errorMessage || (res.httpStatusCode && res.httpStatusCode >= 400) || !res.data) {
        this.error = res.errorMessage || 'Failed to load collection request';
        return;
      }
      this.populateFromRequest(res.data);
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Failed to load collection request';
    } finally {
      this.pageLoading = false;
    }
  }

  private contactToParticipantDetails(value: string): ParticipantDetails | undefined {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    if (trimmed.includes('@')) {
      return { email: trimmed };
    }
    return { phoneNumber: trimmed };
  }

  private buildDto(): CreateCollectionRequestDto {
    const dto: CreateCollectionRequestDto = {
      templateId: this.selectedTemplateId ?? undefined,
    };

    if (this.allowGroup) {
      dto.minimumParticipants = this.minParticipants;
      dto.maximumParticipants = this.maxParticipants;
    }

    const slotCount = this.allowGroup ? this.maxParticipants : 1;
    const list = this.participantContacts
      .slice(0, slotCount)
      .map((c, index) => {
        const details = this.contactToParticipantDetails(c);
        if (!details) {
          return undefined;
        }
        if (this.adminParticipantIndex === index) {
          details.isAdmin = true;
        }
        return details;
      })
      .filter((p): p is ParticipantDetails => p != null && (p.email != null || p.phoneNumber != null));
    if (list.length > 0) {
      dto.participantList = list;
    }

    const trimmedWebhook = this.webhookUrl?.trim();
    if (trimmedWebhook) {
      dto.webhookUrl = trimmedWebhook;
      const secret = this.webhookSecret?.trim();
      if (secret) dto.webhookSecret = secret;
      const identifier = this.webhookIdentifier?.trim();
      if (identifier) dto.webhookIdentifier = identifier;
    }

    return dto;
  }

  onSubmit = async (): Promise<void> => {
    if (this.loading || this.pageLoading) {
      return;
    }
    if (!this.selectedTemplateId) {
      await this.displayService.toast({ message: 'Please select a template.', color: 'warning' });
      return;
    }
    this.loading = true;
    this.error = null;
    try {
      const dto = this.buildDto();
      const res = this.isEditMode
        ? await this.collectionRequestService.update(this.requestId!, dto)
        : await this.collectionRequestService.create(dto);
      if (res.errorMessage || (res.httpStatusCode && res.httpStatusCode >= 400)) {
        this.error = res.errorMessage || `Failed to ${this.isEditMode ? 'update' : 'create'} collection request`;
        return;
      }
      if (res.data) {
        void this.router.navigate(['../self-collect'], {
          relativeTo: this.activatedRoute,
          state: {
            collectionRequest: res.data as CollectionRequest,
            templatePurpose: this.selectedTemplate?.purpose,
          },
        });
        return;
      }
      this.location.back();
    } catch (e) {
      this.error = e instanceof Error ? e.message : `Failed to ${this.isEditMode ? 'update' : 'create'} collection request`;
    } finally {
      this.loading = false;
    }
  }
}
